import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPatch, operationsClient } from '@/lib/api/client';
import { emailChannelEndpoints } from '@/lib/api/endpoints';

export interface ConnectedEmailAccount {
  id: string;
  provider: 'GMAIL' | 'OUTLOOK';
  emailAddress: string;
  status: 'CONNECTED' | 'NEEDS_REAUTH' | 'DISCONNECTED' | 'ERROR';
  displayName: string;
  lastSyncedAt: string | null;
  createdAt: string;
  /**
   * Inbound sync health (NOT-544). `status` is the outbound-send gate and stays
   * CONNECTED even when inbound ingestion is stuck (as long as the OAuth token
   * is valid). When this is false, new mail may not be arriving — show a
   * warning while still allowing the user to send.
   */
  inboundSyncHealthy: boolean;
  /** HTML signature; null when not set. Edited inline on the mailbox card. */
  signatureHtml: string | null;
  /** True when the current user connected (owns) this mailbox; else it is shared with them. */
  isOwner?: boolean;
  /** Owner or tenant admin — may reconnect / disconnect it. Signature is owner-only. */
  canManage?: boolean;
  /** May send from it: owner, or shared with reply access. False = view-only share. */
  canReply?: boolean;
  /** Plain-text fallback for `signatureHtml`. */
  signatureText: string | null;
  /**
   * Per-mailbox enable toggle. When false the composer skips this
   * mailbox's signature regardless of any per-message UI checkbox.
   */
  signatureEnabled: boolean;
}

export interface UpdateMailboxSignaturePayload {
  signatureHtml: string | null;
  signatureText: string | null;
  signatureEnabled: boolean;
}

export const emailAccountsKey = ['email', 'accounts'] as const;

export function useEmailAccounts() {
  return useQuery({
    queryKey: emailAccountsKey,
    queryFn: () =>
      apiGet<{ accounts: ConnectedEmailAccount[] }>(
        operationsClient,
        emailChannelEndpoints.accounts(),
      ).then((r) => r.accounts),
  });
}

/**
 * Sentinel error thrown when the OAuth popup closes without ever
 * posting a result back. Treated as a quiet cancel — onError swallows
 * it instead of toasting a misleading "Failed to connect mailbox".
 */
const CONNECT_CANCELLED = 'EMAIL_CONNECT_CANCELLED';

export type EmailConnectProvider = 'gmail' | 'outlook';

export interface EmailConnectRequest {
  provider: EmailConnectProvider;
  /**
   * The consent popup, opened by the CLICK HANDLER — synchronously, inside
   * the user gesture — and handed in here. Opening it later, after the
   * consent-URL fetch resolved, is outside the gesture: Safari blocks that
   * `window.open` outright (nothing appears, the connect silently never
   * starts) and Chrome does the same under stricter popup settings.
   */
  popup: Window | null;
}

/**
 * Open the OAuth consent popup. Call this directly in the button's onClick
 * and pass the result to `useConnectEmailAccount().mutate(...)` — see
 * {@link EmailConnectRequest.popup} for why it cannot be opened later.
 */
export function openEmailConnectPopup(): Window | null {
  return window.open('about:blank', 'connect-email', 'width=520,height=640,menubar=no,toolbar=no');
}

/** What the OAuth callback page posts back on success. */
export interface EmailConnectResult {
  accountId?: string;
  emailAddress: string;
  /** True on a first connect, false on a reconnect of an existing mailbox. */
  created: boolean;
}

/**
 * Connect a mailbox: take the popup the click opened, fetch the provider
 * consent URL, point the popup at it, and resolve when the OAuth callback
 * posts back `{ type: 'email-oauth' }`. The callback page (served by
 * operations) closes itself. `onConnected` fires after a successful connect —
 * the settings page uses it to ask who else should see a new mailbox.
 */
export function useConnectEmailAccount(options?: {
  onConnected?: (result: EmailConnectResult) => void;
}) {
  const qc = useQueryClient();
  return useMutation<EmailConnectResult, Error, EmailConnectRequest>({
    mutationFn: async ({ provider, popup }) => {
      if (!popup) {
        throw new Error('Popup blocked — allow popups for this site and try again');
      }
      let authUrl: string;
      try {
        ({ authUrl } = await apiGet<{ authUrl: string }>(
          operationsClient,
          emailChannelEndpoints.oauthUrl(provider),
        ));
      } catch (err) {
        // Don't strand a blank popup behind the error toast.
        popup.close();
        throw err;
      }
      popup.location.replace(authUrl);
      // Track whether we ever heard back from the OAuth callback.
      // The callback popup posts `{ type: 'email-oauth', ok, detail }`
      // before closing itself; if the user closes the popup BEFORE
      // that (cancelled / errored consent / wrong tenant), we must
      // not pretend the mailbox connected.
      let outcomeReceived = false;
      return new Promise<EmailConnectResult>((resolve, reject) => {
        const onMessage = (e: MessageEvent) => {
          const data = e.data as {
            type?: string;
            ok?: boolean;
            detail?: string;
            accountId?: string;
            created?: boolean;
          };
          if (data?.type !== 'email-oauth') return;
          outcomeReceived = true;
          window.removeEventListener('message', onMessage);
          clearInterval(poll);
          if (data.ok) {
            resolve({
              accountId: data.accountId,
              emailAddress: data.detail ?? '',
              created: data.created === true,
            });
          } else {
            reject(new Error(data.detail || 'Connect failed'));
          }
        };
        window.addEventListener('message', onMessage);
        const poll = setInterval(() => {
          if (popup.closed) {
            clearInterval(poll);
            window.removeEventListener('message', onMessage);
            if (outcomeReceived) {
              // Race: the postMessage already settled the promise above.
              return;
            }
            // Popup closed without an outcome — cancelled. Reject with a
            // sentinel so onError can stay quiet.
            reject(new Error(CONNECT_CANCELLED));
          }
        }, 700);
      });
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: emailAccountsKey });
      toast.success('Mailbox connected');
      options?.onConnected?.(result);
    },
    onError: (err) => {
      if (err.message === CONNECT_CANCELLED) return;
      toast.error(err.message || 'Failed to connect mailbox');
    },
  });
}

export function useDisconnectEmailAccount() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiDelete(operationsClient, emailChannelEndpoints.disconnect(id)).then(() => undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: emailAccountsKey });
      toast.success('Mailbox disconnected');
    },
    onError: () => toast.error('Failed to disconnect mailbox'),
  });
}

/**
 * Save the per-mailbox signature + per-mailbox enable toggle. On
 * success the email-accounts cache is patched in place so the inbox
 * composer (which reads the same cache via the active mailbox) picks
 * up the new signature without a refetch.
 */
export function useUpdateMailboxSignature(accountId: string) {
  const qc = useQueryClient();
  return useMutation<ConnectedEmailAccount, Error, UpdateMailboxSignaturePayload>({
    mutationFn: async (payload) => {
      const updated = await apiPatch<ConnectedEmailAccount, UpdateMailboxSignaturePayload>(
        operationsClient,
        emailChannelEndpoints.updateSignature(accountId),
        payload,
      );
      return updated;
    },
    onSuccess: (updated) => {
      qc.setQueryData<ConnectedEmailAccount[]>(emailAccountsKey, (old) =>
        (old ?? []).map((a) => (a.id === accountId ? updated : a)),
      );
    },
  });
}
